import { useMemo } from 'react';
import { IImageThumbnail } from '../../../Entities';
import { ImageCategory } from '../../shared/state/utils';
import { IExtraImageCost, UploadableProductData } from '../../listImages/queries/use-query-images';

type ProductCategoryCounts = {
    deliveredCount: number;
    extraImageCount: number;
};

type UseProductCategoryChangesArgs = {
    uploadableProducts: UploadableProductData[];
    getEffectiveCategory: (image: IImageThumbnail) => number;
};

type UseProductCategoryChangesResult = {
    totalExtraImageCount: number;
    totalExtraImageCost: IExtraImageCost | null;
    totalDeliveredCount: number;
    getProductExtraImageCount: (productId: number) => number;
};

export function useProductCategoryChanges({
    uploadableProducts,
    getEffectiveCategory
}: UseProductCategoryChangesArgs): UseProductCategoryChangesResult {
    let { countsMap, totalExtraImageCount, totalExtraImageCost, totalDeliveredCount } = useMemo(
        function computeProductCategoryChanges() {
            let countsMap = new Map<number, ProductCategoryCounts>();
            let totalExtra = 0;
            let totalDelivered = 0;

            if (!uploadableProducts) {
                return {
                    countsMap,
                    totalExtraImageCount: 0,
                    totalExtraImageCost: null as IExtraImageCost | null,
                    totalDeliveredCount: 0
                };
            }

            for (let product of uploadableProducts) {
                let deliveredCount = 0;
                let extraToNormalCount = 0;
                let normalToExtraCount = 0;

                for (let image of product.thumbnails) {
                    let effectiveCategory = getEffectiveCategory(image);

                    if (effectiveCategory === ImageCategory.Normal) {
                        deliveredCount++;
                    }

                    if (
                        image.categoryId === ImageCategory.Extra &&
                        effectiveCategory === ImageCategory.Normal
                    ) {
                        extraToNormalCount++;
                    } else if (
                        image.categoryId === ImageCategory.Normal &&
                        effectiveCategory === ImageCategory.Extra
                    ) {
                        normalToExtraCount++;
                    }
                }

                let extraImageCount = Math.max(0, extraToNormalCount - normalToExtraCount);

                countsMap.set(product.id, { deliveredCount, extraImageCount });
                totalExtra += extraImageCount;
                totalDelivered += deliveredCount;
            }

            let totalExtraImageCost: IExtraImageCost | null =
                uploadableProducts.length > 0 ? uploadableProducts[0].extraImageCost : null;

            return {
                countsMap,
                totalExtraImageCount: totalExtra,
                totalExtraImageCost,
                totalDeliveredCount: totalDelivered
            };
        },
        [uploadableProducts, getEffectiveCategory]
    );

    function getProductExtraImageCount(productId: number): number {
        let counts = countsMap.get(productId);
        return counts ? counts.extraImageCount : 0;
    }

    return {
        totalExtraImageCount,
        totalExtraImageCost,
        totalDeliveredCount,
        getProductExtraImageCount
    };
}
